/**
 * RequestInfoLinksTab.tsx
 *
 * Marketing → Templates → Request-Info Links.
 *
 * The LIST of every link the account has made, with a search and "New link".
 * New link, Edit and a row click open the builder page
 * (RequestInfoLinkBuilderPage, /marketing/templates/links/:id), which generates
 * a link or edits an existing one's note, top image and logo beside a live
 * preview — its own page, like the email and SMS template builders.
 *
 * A link points at ONE resource (a product, package, appointment type or
 * membership). Whoever taps it lands on a branded page for that resource and can
 * ask for more information in one tap, which creates a lead credited to the
 * message that delivered it. It is resource-level on purpose: one link can sit
 * in many messages, and the send pipeline appends a signed per-recipient suffix
 * at send time.
 *
 * Links are never deleted. Deactivating one keeps the page up with the
 * facility's phone number in place of the button, and it can be reactivated.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, Copy, Eye, Link as LinkIcon, Pencil, Plus, RotateCcw, Search } from 'lucide-react';
import { Badge, Button, DataTable, Input, Spinner } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { formatDate } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import * as marketingSvc from '@/services/marketing.service';
import type { RequestInfoLink, RequestInfoLinksResponse } from '@/services/marketing.service';
import { KIND_LABEL, REQUEST_INFO_LINKS_QUERY_KEY, requestInfoLinkBuilderPath } from './requestInfoLinkResources';

/** What the search box matches — only what the list shows: the resource, its kind and the link code. */
function matchesSearch(link: RequestInfoLink, query: string): boolean {
  return [link.resourceName, KIND_LABEL[link.resourceType] ?? '', link.code]
    .some((value) => value.toLowerCase().includes(query));
}

export default function RequestInfoLinksTab() {
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<RequestInfoLink | null>(null);

  const linksQuery = useQuery({
    queryKey: REQUEST_INFO_LINKS_QUERY_KEY,
    queryFn: () => marketingSvc.listRequestInfoLinks(),
  });

  const links = linksQuery.data?.links ?? [];
  const query = search.trim().toLowerCase();
  const visibleLinks = query ? links.filter((l) => matchesSearch(l, query)) : links;
  const openBuilder = (id: number | 'new') => navigate(requestInfoLinkBuilderPath(id));

  /** Swaps one link in the cached list in place: an edit or a (de)activation cannot move lead counts. */
  function patchCachedLink(id: number, next: Partial<RequestInfoLink>) {
    qc.setQueryData<RequestInfoLinksResponse>(REQUEST_INFO_LINKS_QUERY_KEY, (old) =>
      old ? { ...old, links: old.links.map((l) => (l.id === id ? { ...l, ...next } : l)) } : old,
    );
  }

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      marketingSvc.setRequestInfoLinkActive(id, active),
    onSuccess: (res, vars) => {
      // Patched in place from the response, so no refetch of the whole list.
      patchCachedLink(vars.id, { ...(res?.link ?? {}), active: res?.link?.active ?? vars.active });
      toast.success(vars.active ? 'Link reactivated' : 'Link deactivated');
    },
    onError: (err, vars) =>
      toast.error(getErrorMessage(err, vars.active ? 'Could not reactivate the link' : 'Could not deactivate the link')),
  });

  /** The landing page as a recipient would see it, in preview: nothing is created from it. */
  function previewLink(link: RequestInfoLink) {
    window.open(link.previewUrl || link.url, '_blank', 'noopener,noreferrer');
  }

  async function copyLink(link: RequestInfoLink) {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiedId(link.id);
      window.setTimeout(() => setCopiedId((current) => (current === link.id ? null : current)), 1500);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link', 'Select it and copy it manually.');
    }
  }

  async function confirmDeactivate() {
    if (!deactivateTarget) return;
    try {
      await toggleMut.mutateAsync({ id: deactivateTarget.id, active: false });
    } catch {
      /* surfaced by toggleMut.onError */
    }
  }

  const columns: DataTableColumn<RequestInfoLink>[] = [
    {
      key: 'resourceName',
      header: 'Resource',
      role: 'title',
      render: (l) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-slate-900" title={l.resourceName}>
            {l.resourceName || 'Untitled'}
          </div>
          <div className="text-xs text-slate-500">{KIND_LABEL[l.resourceType] ?? l.resourceType}</div>
        </div>
      ),
    },
    {
      key: 'url',
      header: 'Link',
      render: (l) => (
        <span className="break-all font-mono text-xs text-slate-600" title={l.url}>
          {`/l/${l.code}`}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      width: 'whitespace-nowrap',
      render: (l) => <span className="text-sm text-slate-600">{formatDate(l.createdAt)}</span>,
    },
    {
      key: 'leadCount',
      header: 'Leads',
      align: 'right',
      render: (l) => (
        <span className="text-sm font-semibold tabular-nums text-slate-800">
          {(l.leadCount ?? 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (l) => (
        <Badge variant={l.active ? 'success' : 'neutral'} dot size="sm">
          <span title={!l.active && l.deactivatedAt ? `Deactivated ${formatDate(l.deactivatedAt)}` : undefined}>
            {l.active ? 'Active' : 'Inactive'}
          </span>
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      label: 'Actions',
      align: 'right',
      render: (l) => {
        const pendingThis = toggleMut.isPending && toggleMut.variables?.id === l.id;
        const copiedThis = copiedId === l.id;
        // Icon-only to keep the column narrow; the title is the hover tooltip and
        // the aria-label is what a screen reader announces.
        return (
          <div className="flex justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              leftIcon={<Pencil className="h-4 w-4" />}
              onClick={(e) => {
                e.stopPropagation();
                openBuilder(l.id);
              }}
              title="Edit"
              aria-label={`Edit link for ${l.resourceName}`}
            />
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              leftIcon={copiedThis ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              onClick={(e) => {
                e.stopPropagation();
                void copyLink(l);
              }}
              title={copiedThis ? 'Copied' : 'Copy link'}
              aria-label={`Copy link for ${l.resourceName}`}
            />
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              leftIcon={<Eye className="h-4 w-4" />}
              onClick={(e) => {
                e.stopPropagation();
                previewLink(l);
              }}
              title="Preview page"
              aria-label={`Preview landing page for ${l.resourceName}`}
            />
            {l.active ? (
              <Button
                size="sm"
                variant="ghost"
                iconOnly
                leftIcon={<Ban className="h-4 w-4" />}
                disabled={pendingThis}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeactivateTarget(l);
                }}
                title="Deactivate"
                aria-label={`Deactivate link for ${l.resourceName}`}
              />
            ) : (
              <Button
                size="sm"
                variant="ghost"
                iconOnly
                leftIcon={<RotateCcw className="h-4 w-4" />}
                loading={pendingThis}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMut.mutate({ id: l.id, active: true });
                }}
                title="Reactivate"
                aria-label={`Reactivate link for ${l.resourceName}`}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-5 pt-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Input
              type="search"
              aria-label="Search links"
              placeholder="Search by resource or link code…"
              leftAddon={<Search className="h-4 w-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              wrapperClassName="w-full sm:max-w-sm"
            />
            {links.length > 0 && (
              <span className="flex-shrink-0 text-sm text-slate-500">
                {query ? `${visibleLinks.length} of ${links.length}` : links.length}{' '}
                {links.length === 1 && !query ? 'link' : 'links'}
              </span>
            )}
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => openBuilder('new')}>
            New link
          </Button>
        </div>

        {linksQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : linksQuery.isError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-6 text-center">
            <p className="text-sm text-red-700">
              {getErrorMessage(linksQuery.error, 'Could not load request-info links.')}
            </p>
            <Button className="mt-3" size="sm" variant="secondary" onClick={() => void linksQuery.refetch()}>
              Try again
            </Button>
          </div>
        ) : links.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white py-16 text-center">
            <LinkIcon className="mx-auto mb-4 h-12 w-12 text-slate-200" />
            <h3 className="mb-1 text-lg font-semibold text-slate-700">No request-info links yet</h3>
            <p className="text-sm text-slate-400">
              Create one to add a one-tap “request more info” page to your messages.
            </p>
            <Button className="mt-4" leftIcon={<Plus className="h-4 w-4" />} onClick={() => openBuilder('new')}>
              New link
            </Button>
          </div>
        ) : visibleLinks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center">
            <p className="text-sm text-slate-500">No links match “{search.trim()}”.</p>
            <Button className="mt-3" size="sm" variant="secondary" onClick={() => setSearch('')}>
              Clear search
            </Button>
          </div>
        ) : (
          <DataTable<RequestInfoLink>
            data={visibleLinks}
            columns={columns}
            keyField="id"
            onRowClick={(l) => openBuilder(l.id)}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deactivateTarget}
        title="Deactivate link"
        message={
          <>
            Deactivate the link for “{deactivateTarget?.resourceName}”? People who open it will see your
            phone number instead of the request button, and no new leads will come from it. Messages already
            sent keep the link, and you can reactivate it at any time.
          </>
        }
        confirmLabel="Deactivate"
        variant="warning"
        onConfirm={confirmDeactivate}
        onClose={() => setDeactivateTarget(null)}
      />
    </div>
  );
}
